import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as adminApi from '@/services/admin.api';
import { showSuccess, showError } from '@/utils/toast';

// Users
export function useAdminUsers(filters: { page?: number; perPage?: number; search?: string; role?: string; status?: string }) {
  return useQuery({ queryKey: ['adminUsers', filters], queryFn: () => adminApi.getAdminUsers(filters) });
}

export function useAdminUser(userId: string) {
  return useQuery({ queryKey: ['adminUser', userId], queryFn: () => adminApi.getAdminUser(userId), enabled: !!userId });
}

export function useCreateUser() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.createAdminUser,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUsers'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useUpdateUser() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: Record<string, unknown> }) => adminApi.updateAdminUser(userId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUsers'] }); qc.invalidateQueries({ queryKey: ['adminUser'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useUpdateUserStatus() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: string }) => adminApi.updateAdminUserStatus(userId, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUsers'] }); qc.invalidateQueries({ queryKey: ['adminUser'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useDeleteUser() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.deleteAdminUser(userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUsers'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useResetPassword() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) => adminApi.adminResetPassword(userId, newPassword),
    onSuccess: () => showSuccess(t('common.success')),
    onError: () => showError(t('common.error')),
  });
}

// Subscriptions
export function useAdminSubscriptions(filters: { page?: number; perPage?: number; status?: string; plan?: string; expiringDays?: number }) {
  return useQuery({ queryKey: ['adminSubs', filters], queryFn: () => adminApi.getAdminSubscriptions(filters) });
}

export function useExtendSubscription() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) => adminApi.extendSubscription(userId, days),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSubs'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useChangeSubscriptionPlan() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { plan: string; maxSites?: number; priceAmount?: number } }) => adminApi.changeSubscriptionPlan(userId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSubs'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

// Sites management
export function useAdminSites(filters?: { page?: number; perPage?: number; search?: string; status?: string }) {
  return useQuery({ queryKey: ['adminSites', filters], queryFn: () => adminApi.getAdminSites(filters) });
}

export function useConnectSite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.connectSite,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSites'] }); showSuccess(t('sites.connectionSuccess')); },
    onError: () => showError(t('sites.connectionFailed')),
  });
}

export function useUpdateSite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: { name?: string; url?: string; wcConsumerKey?: string; wcConsumerSecret?: string; bridgeApiKey?: string } }) => adminApi.updateSite(siteId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSites'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useDeleteSite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => adminApi.deleteSite(siteId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSites'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useTestSiteConnection() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (siteId: string) => adminApi.testSiteConnection(siteId),
    onSuccess: () => showSuccess(t('sites.testSuccess')),
    onError: () => showError(t('sites.testFailed')),
  });
}

// Site user assignments & permissions
export function useAssignUserToSite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) => adminApi.assignUserToSite(siteId, userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUser'] }); qc.invalidateQueries({ queryKey: ['adminSites'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useUnassignUserFromSite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) => adminApi.unassignUserFromSite(siteId, userId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUser'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useUpdateSitePermissions() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userId, permissions }: { siteId: string; userId: string; permissions: Record<string, boolean> }) => adminApi.updateSitePermissions(siteId, userId, permissions),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminUser'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

// Bridge plugin info
export function useBridgePluginInfo() {
  return useQuery({ queryKey: ['bridgePluginInfo'], queryFn: adminApi.getBridgePluginInfo });
}

// Plugins
export function useAdminPlugins() {
  return useQuery({ queryKey: ['adminPlugins'], queryFn: adminApi.getAdminPlugins });
}

export function useCreatePlugin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.createAdminPlugin,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminPlugins'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useUpdatePlugin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pluginId, data }: { pluginId: string; data: Record<string, unknown> }) => adminApi.updateAdminPlugin(pluginId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminPlugins'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

export function useDeletePlugin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.deleteAdminPlugin,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminPlugins'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}

// AI Usage
export function useAdminAIUsage() {
  return useQuery({ queryKey: ['adminAIUsage'], queryFn: adminApi.getAdminAIUsage });
}

// Audit Log
export function useAdminAuditLog(filters: { page?: number; perPage?: number; userId?: string; action?: string; from?: string; to?: string }) {
  return useQuery({ queryKey: ['adminAuditLog', filters], queryFn: () => adminApi.getAdminAuditLog(filters) });
}

// Role Default Permissions
export function useRoleDefaults() {
  return useQuery({ queryKey: ['roleDefaults'], queryFn: adminApi.getRoleDefaults });
}

export function useUpdateRoleDefaults() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, Record<string, boolean>>) => adminApi.updateRoleDefaults(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roleDefaults'] }); showSuccess(t('admin.roleDefaultsSaved')); },
    onError: () => showError(t('common.error')),
  });
}

// App Settings
export function useAdminSettings() {
  return useQuery({ queryKey: ['adminSettings'], queryFn: adminApi.getAdminSettings });
}

export function useUpdateSetting() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => adminApi.updateAdminSetting(key, value),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adminSettings'] }); showSuccess(t('common.success')); },
    onError: () => showError(t('common.error')),
  });
}
